import React from 'react';
import { Button } from './ui/button';
import { ArrowLeft, Trash2, Mail, ShieldCheck, Clock } from 'lucide-react';

const LOGO_FALLBACK = 'https://customer-assets.emergentagent.com/job_grocery-saver-4/artifacts/boaapnb1_image%20%281%29.png';

const DeleteAccount = ({ onBack }) => {
  const logoUrl = process.env.REACT_APP_LOGO_URL || LOGO_FALLBACK;
  const handleLogoError = (e) => {
    if (e.target.src !== LOGO_FALLBACK) e.target.src = LOGO_FALLBACK;
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
            <div className="flex items-center gap-3">
              <img
                src={logoUrl}
                alt="SaleSavor App Icon"
                className="h-8 w-8 rounded-lg"
                onError={handleLogoError}
              />
              <span className="text-xl font-bold" style={{ color: '#2c5f2d' }}>
                SaleSavor
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F5E9' }}>
              <Trash2 className="h-6 w-6" style={{ color: '#2C5F2D' }} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1a1a1a' }}>
              Delete Your Account &amp; Data
            </h1>
          </div>
          <p className="text-sm mb-8" style={{ color: '#888888' }}>Last updated: November 27, 2025</p>

          <p className="text-base leading-relaxed mb-8" style={{ color: '#1A1A1A' }}>
            You can permanently delete your SaleSavor account and all associated personal data at any time.
            This page explains how to request deletion and what information is removed. No account login is
            required to read these instructions.
          </p>

          {/* Option 1: In-app */}
          <div className="rounded-xl border p-5 sm:p-6 mb-6" style={{ borderColor: '#cfe8d0', backgroundColor: '#E8F5E9' }}>
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5" style={{ color: '#2c5f2d' }} />
              <h2 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>Option 1 — Delete in the app (fastest)</h2>
            </div>
            <ol className="list-decimal pl-5 space-y-2 text-base" style={{ color: '#1A1A1A' }}>
              <li>Open the <strong>SaleSavor</strong> app on your device.</li>
              <li>Open <strong>Settings</strong>, scroll to the bottom, tap <strong>Delete Account</strong> and confirm.</li>
            </ol>
            <p className="text-sm mt-3" style={{ color: '#777777' }}>
              Your account and data are removed immediately when you confirm in the app.
            </p>
          </div>

          {/* Option 2: Email */}
          <div className="rounded-xl border p-5 sm:p-6 mb-8" style={{ borderColor: '#e2e2e2', backgroundColor: '#fafafa' }}>
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-5 w-5" style={{ color: '#2c5f2d' }} />
              <h2 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>Option 2 — Request deletion by email</h2>
            </div>
            <p className="text-base mb-2" style={{ color: '#1A1A1A' }}>
              Send an email to{' '}
              <a href="mailto:info@salesavor.ca?subject=Delete%20My%20Account" className="font-semibold underline" style={{ color: '#2c5f2d' }}>
                info@salesavor.ca
              </a>{' '}
              with the subject line <strong>&quot;Delete My Account&quot;</strong>.
            </p>
            <p className="text-base" style={{ color: '#1A1A1A' }}>
              Please send your request from the <strong>email address registered to your account</strong> so we can
              verify your identity.
            </p>
          </div>

          {/* What gets deleted */}
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1a1a1a' }}>What data is deleted</h2>
          <p className="text-base mb-3" style={{ color: '#1A1A1A' }}>
            When your account is deleted, we permanently remove all data associated with it, including:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-base mb-8" style={{ color: '#1A1A1A' }}>
            <li>Your account and login credentials (name, email, user ID)</li>
            <li>Your profile and preferences (dietary preferences, health concerns, family size, meal preferences)</li>
            <li>Saved recipes and recipe history</li>
            <li>Shopping lists and saved sales</li>
            <li>Active sessions and authentication tokens</li>
          </ul>

          {/* Timeframe */}
          <div className="rounded-xl border p-5 sm:p-6 mb-8 flex items-start gap-3" style={{ borderColor: '#cfe8d0', backgroundColor: '#E8F5E9' }}>
            <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#2C5F2D' }} />
            <div>
              <h2 className="text-lg font-bold mb-1" style={{ color: '#1a1a1a' }}>Processing time</h2>
              <p className="text-base" style={{ color: '#1A1A1A' }}>
                In-app deletions are processed immediately. Email deletion requests are completed within
                <strong> 30 days</strong> of verification. Deletion is <strong>permanent and cannot be undone</strong>.
              </p>
            </div>
          </div>

          <p className="text-sm" style={{ color: '#777777' }}>
            Questions about deleting your data? Contact us at{' '}
            <a href="mailto:info@salesavor.ca" className="font-semibold underline" style={{ color: '#2c5f2d' }}>
              info@salesavor.ca
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
